import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import axios from 'axios';
import MetaData from '../Layouts/MetaData';
import { emptyCart, removePromoCode } from '../../actions/cartAction';

const stripePromise = loadStripe('pk_live_xWzWSyu3bi0iBdbBeqftvUdn');

const AlipayReturnInner = () => {
    const stripe = useStripe();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // checking | failed | missingDetails | orderFailed
    const [status, setStatus] = useState('checking');

    useEffect(() => {
        if (!stripe) return;

        const finish = async () => {
            const params = new URLSearchParams(window.location.search);
            const clientSecret = params.get('payment_intent_client_secret');

            if (!clientSecret) {
                setStatus('failed');
                return;
            }

            const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);

            if (error || !paymentIntent || paymentIntent.status !== 'succeeded') {
                setStatus('failed');
                return;
            }

            const pendingRaw = sessionStorage.getItem('alipayPendingOrder');
            if (!pendingRaw) {
                // Payment succeeded but the order details are missing (e.g. this
                // link was opened on a different device/browser). The customer
                // WAS charged, so this must not fail silently.
                setStatus('missingDetails');
                return;
            }

            try {
                const pending = JSON.parse(pendingRaw);
                const { data: orderData } = await axios.post('/api/v1/order/new', {
                    ...pending,
                    paymentInfo: { id: paymentIntent.id, status: paymentIntent.status },
                    paymentMethod: 'alipay',
                    orderSource: 'website',
                });

                sessionStorage.removeItem('alipayPendingOrder');
                dispatch(emptyCart());
                dispatch(removePromoCode());

                navigate('/orders/success', { state: { orderNumber: orderData.order.orderNumber } });
            } catch (err) {
                setStatus('orderFailed');
            }
        };

        finish();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stripe]);

    const paymentIntentId = new URLSearchParams(window.location.search).get('payment_intent');

    if (status === 'checking') {
        return (
            <main className="w-full mt-20">
                <div className="flex flex-col gap-2 items-center justify-center sm:w-4/6 sm:mt-4 m-auto mb-7 bg-white shadow rounded p-6 pb-12">
                    <h1 className="text-xl font-semibold">Confirming your Alipay payment…</h1>
                    <p className="text-gray-600 mt-2">Please don't close this page.</p>
                </div>
            </main>
        );
    }

    if (status === 'missingDetails' || status === 'orderFailed') {
        return (
            <main className="w-full mt-20">
                <div className="flex flex-col gap-2 items-center justify-center sm:w-4/6 sm:mt-4 m-auto mb-7 bg-white shadow rounded p-6 pb-12">
                    <h1 className="text-xl font-semibold text-red-600">Payment received, order incomplete</h1>
                    <p className="text-gray-600 mt-2 text-center">
                        Your Alipay payment went through, but we couldn't automatically finish creating your order.
                        Please contact support with this reference: <span className="font-mono">{paymentIntentId}</span>
                    </p>
                </div>
            </main>
        );
    }

    return (
        <main className="w-full mt-20">
            <div className="flex flex-col gap-2 items-center justify-center sm:w-4/6 sm:mt-4 m-auto mb-7 bg-white shadow rounded p-6 pb-12">
                <h1 className="text-xl font-semibold text-red-600">Payment not completed</h1>
                <p className="text-gray-600 mt-2">Your Alipay payment wasn't completed. No order was created and you haven't been charged.</p>
                <Link to="/process/payment" className="bg-primary-blue mt-4 py-2.5 px-6 text-white uppercase shadow hover:shadow-lg rounded-sm">Try again</Link>
            </div>
        </main>
    );
};

const AlipayReturn = () => (
    <>
        <MetaData title="Confirming Payment" />
        <Elements stripe={stripePromise}>
            <AlipayReturnInner />
        </Elements>
    </>
);

export default AlipayReturn;